import {ApiConfig} from '../config/ApiConfig';
import {getAccessToken} from '../helpers/storage';
import {filterForKind} from '../helpers/sipCallEvents';

/**
 * VoIP backend client (`/api/voip/*`). Mirrors the raw-fetch + Bearer pattern
 * used across this app (see notificationApi.js) — the services-app has no
 * swagger client.
 */

const BASE = `${ApiConfig.apiURL}/api/voip`;

const authHeaders = async () => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        const authError = new Error('Authentication required');
        authError.code = 'AUTH_REQUIRED';
        throw authError;
    }

    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
    };
};

const parse = async (response) => {
    if (!response.ok) {
        let backendMessage = '';

        try {
            const text = await response.text();

            if (text) {
                const parsed = JSON.parse(text);
                backendMessage = parsed?.message || parsed?.error || '';
            }
        } catch (_error) {
            backendMessage = '';
        }

        const err = new Error(backendMessage || `VoIP request failed (${response.status})`);
        err.status = response.status;
        if (response.status === 401 || response.status === 403) {
            err.code = 'AUTH_REQUIRED';
        }
        throw err;
    }
    if (response.status === 204) {
        return true;
    }
    const text = await response.text();
    if (!text) {
        return true;
    }
    try {
        return JSON.parse(text);
    } catch (_error) {
        return text;
    }
};

const unwrapData = (payload) => (payload && typeof payload === 'object' && payload.data !== undefined ? payload.data : payload);

const buildQuery = (params = {}) => {
    const entries = Object.entries(params).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
    );
    if (entries.length === 0) {
        return '';
    }
    return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&')}`;
};

/**
 * Register the Apple PushKit VoIP token (its own device row, platform
 * `ios-voip`) so incoming-call VoIP pushes reach a backgrounded/killed app.
 *
 * @param {{token: string, deviceId?: string, deviceName?: string, appVersion?: string, osVersion?: string, platform?: string}} payload
 * @returns {Promise<any>}
 */
export async function registerVoipDeviceToken(payload) {
    const response = await fetch(`${BASE}/device-token`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({platform: 'ios-voip', ...payload}),
    });
    return parse(response);
}

/**
 * Unregister the PushKit VoIP token (logout).
 * @param {{token?: string, deviceId?: string}} payload
 */
export async function unregisterVoipDeviceToken(payload) {
    const response = await fetch(`${BASE}/device-token`, {
        method: 'DELETE',
        headers: await authHeaders(),
        body: JSON.stringify(payload || {}),
    });
    return parse(response);
}

/**
 * Reverse-lookup the caller identity for a number (screen-pop). The backend
 * searches all store data; with `ai` it may add an unverified AI guess.
 * @param {string} phone
 * @param {('cached'|'run'|'refresh'|'off')} [ai] AI mode: `cached` instant, `run` web-searches when nothing is stored, `refresh` re-searches.
 * @returns {Promise<{match: (object|null), matches?: object[], suggestion?: (object|null), suggestionStatus?: string}>}
 */
export async function lookupNumber(phone, ai) {
    const response = await fetch(`${BASE}/lookup${buildQuery(ai ? {phone, ai} : {phone})}`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    return parse(response);
}

/**
 * Softphone session config for the active connection (SIP creds / Twilio token).
 * @returns {Promise<object>}
 */
export async function getRtcConfig() {
    const response = await fetch(`${BASE}/rtc-config`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    return unwrapData(await parse(response));
}

/**
 * Short-lived signed URL of a call's stored recording (provider- or app-recorded).
 * Rejects with `err.status` 404 (no recording) / 409 (`err.message` explains pending / failed).
 * @param {string} callId Call log id.
 * @returns {Promise<{url: string, expiresAt: string, contentType: string, fileName: string}>}
 */
export async function getCallRecording(callId) {
    const response = await fetch(`${BASE}/calls/${encodeURIComponent(callId)}/recording`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    return unwrapData(await parse(response));
}

/**
 * REST click-to-call fallback.
 * @param {string} to Destination number.
 */
export async function dial(to) {
    const response = await fetch(`${BASE}/dial`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({to}),
    });
    return parse(response);
}

/** Paginated call history. */
export async function listCalls(params = {}) {
    const response = await fetch(`${BASE}/calls${buildQuery(params)}`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    return parse(response);
}

/**
 * Call history limited server-side to the user's OWN SIP account(s) (`scope=sip`
 * — other agents' calls never leave the backend). Empty when the user has no
 * SIP connection.
 *
 * @param {{kind?: ('started'|'received'|'missed'), search?: string, from?: string, to?: string, page?: number, limit?: number}} [params]
 *   `started` = outgoing, `received` = answered incoming, `missed` = not received; omit for all.
 * @returns {Promise<object>} Paginated call list.
 */
export async function listSipScopedCalls({kind, ...rest} = {}) {
    return listCalls({...rest, filter: filterForKind(kind), scope: 'sip'});
}

/** Recent counterparts (recall list). */
export async function recentCalls(params = {}) {
    const response = await fetch(`${BASE}/recent-calls${buildQuery(params)}`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    return parse(response);
}

/** Unified directory search (Névjegyzék + CRM). */
export async function searchDirectory(params = {}) {
    const response = await fetch(`${BASE}/directory${buildQuery(params)}`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    return parse(response);
}

/** Provider preset catalogue for the settings screen. */
export async function getProviders() {
    const response = await fetch(`${BASE}/providers`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    return parse(response);
}

/** List the user's VoIP connections. */
export async function listConnections() {
    const response = await fetch(`${BASE}/connections`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    return parse(response);
}

/** Create/update a VoIP connection. */
export async function upsertConnection(payload) {
    const response = await fetch(`${BASE}/connections`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(payload),
    });
    return parse(response);
}

/** Verify a VoIP connection. */
export async function verifyConnection(id) {
    const response = await fetch(`${BASE}/connections/${id}/verify`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({}),
    });
    return unwrapData(await parse(response));
}

/**
 * Delete a VoIP connection.
 * @param {string} id Connection id.
 * @returns {Promise<any>}
 */
export async function deleteConnection(id) {
    const response = await fetch(`${BASE}/connections/${id}`, {
        method: 'DELETE',
        headers: await authHeaders(),
    });
    return parse(response);
}

/* -------------------------------------------------------------------------- */
/*  Manual Névjegyzék contacts CRUD                                            */
/* -------------------------------------------------------------------------- */

/**
 * List the user's manual Névjegyzék contacts (paginated).
 * @param {{search?: string, page?: number, limit?: number}} [params]
 * @returns {Promise<object>}
 */
export async function listContacts(params = {}) {
    const response = await fetch(`${BASE}/contacts${buildQuery(params)}`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    return parse(response);
}

/**
 * Create a manual Névjegyzék contact.
 * @param {{name: string, company?: string, email?: string, note?: string, favorite?: boolean, shared?: boolean, phones?: Array<{label?: string, phone: string}>}} payload
 * @returns {Promise<object>}
 */
export async function createContact(payload) {
    const response = await fetch(`${BASE}/contacts`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(payload),
    });
    return parse(response);
}

/**
 * Update a manual Névjegyzék contact.
 * @param {string} id Contact id.
 * @param {object} payload Partial contact fields.
 * @returns {Promise<object>}
 */
export async function updateContact(id, payload) {
    const response = await fetch(`${BASE}/contacts/${id}`, {
        method: 'PUT',
        headers: await authHeaders(),
        body: JSON.stringify(payload),
    });
    return parse(response);
}

/**
 * Delete a manual Névjegyzék contact.
 * @param {string} id Contact id.
 * @returns {Promise<any>}
 */
export async function deleteContact(id) {
    const response = await fetch(`${BASE}/contacts/${id}`, {
        method: 'DELETE',
        headers: await authHeaders(),
    });
    return parse(response);
}

/* -------------------------------------------------------------------------- */
/*  Call credits (minute balance, packages, top-up, automation settings)       */
/* -------------------------------------------------------------------------- */

/**
 * Current VoIP minute balance + low-balance flag for a store.
 * @param {string} [restaurantId]
 * @returns {Promise<{credits: number, lowBalanceThreshold: number, low: boolean, unitPriceHuf: number, perMinute: number}>}
 */
export async function getCreditStatus(restaurantId) {
    const response = await fetch(`${BASE}/credits/status${buildQuery({restaurantId})}`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    return parse(response);
}

/**
 * Purchasable VoIP minute packages.
 * @param {string} [restaurantId]
 * @returns {Promise<{packages: Array<object>}>}
 */
export async function getCreditPackages(restaurantId) {
    const response = await fetch(`${BASE}/credits/packages${buildQuery({restaurantId})}`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    return parse(response);
}

/**
 * Start a Viva checkout for a VoIP minute top-up.
 * @param {{restaurantId?: string, packageId?: string, credits?: number}} payload
 * @returns {Promise<{checkoutUrl?: string, needsManual?: boolean}>}
 */
export async function purchaseCredits(payload) {
    const response = await fetch(`${BASE}/credits/purchase`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(payload || {}),
    });
    return parse(response);
}

/**
 * Read the VoIP credit automation settings (low-balance threshold + auto top-up).
 * @param {string} [restaurantId]
 * @returns {Promise<object>}
 */
export async function getCreditSettings(restaurantId) {
    const response = await fetch(`${BASE}/credits/settings${buildQuery({restaurantId})}`, {
        method: 'GET',
        headers: await authHeaders(),
    });
    return parse(response);
}

/**
 * Update the VoIP credit automation settings.
 * @param {{restaurantId?: string, lowBalanceThreshold?: number, autoTopup?: {enabled?: boolean, threshold?: number, packageId?: string, creditAmount?: number}}} payload
 * @returns {Promise<object>}
 */
export async function updateCreditSettings(payload) {
    const response = await fetch(`${BASE}/credits/settings`, {
        method: 'PATCH',
        headers: await authHeaders(),
        body: JSON.stringify(payload || {}),
    });
    return parse(response);
}

/* -------------------------------------------------------------------------- */
/*  Metered call session (OUTBOUND only — incoming is free, no session)        */
/* -------------------------------------------------------------------------- */

/**
 * Open a metered outbound call session. Throws an error with `status === 402`
 * when the store is out of credits (caller must block the call + prompt a top-up).
 * @param {{to: string, from?: string, direction?: 'outgoing'|'incoming', providerKey?: string, restaurantId?: string}} payload
 * @returns {Promise<{sessionId: string, balance: number, metered: boolean}>}
 */
export async function startCallSession(payload) {
    const response = await fetch(`${BASE}/calls/session`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({direction: 'outgoing', ...payload}),
    });
    return parse(response);
}

/**
 * Per-minute metering heartbeat (deducts one credit). `exhausted: true` means
 * the balance hit zero and the caller must hang up.
 * @param {string} sessionId
 * @param {string} [restaurantId]
 * @returns {Promise<{exhausted: boolean, balance: number}>}
 */
export async function meterCallSession(sessionId, restaurantId) {
    const response = await fetch(`${BASE}/calls/session/${sessionId}/meter`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(restaurantId ? {restaurantId} : {}),
    });
    return parse(response);
}

/**
 * Close a metered call session (final reconcile with the real duration).
 * @param {string} sessionId
 * @param {{durationSeconds?: number, restaurantId?: string}} [payload]
 * @returns {Promise<object>}
 */
export async function endCallSession(sessionId, payload = {}) {
    const response = await fetch(`${BASE}/calls/session/${sessionId}/end`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(payload || {}),
    });
    return parse(response);
}
